// Pure, testable logic for the Legacy Score — a 100-point family legacy
// readiness framework across six dimensions. No I/O, safe on server + client.

export type Dimension =
  | "protection"
  | "income"
  | "retirement"
  | "estate"
  | "beneficiaries"
  | "education";

export const DIMENSION_META: Record<
  Dimension,
  { label: string; tagline: string; weight: number; icon: string }
> = {
  protection: {
    label: "Protection",
    tagline: "Life coverage adequacy for income, debt, and education.",
    weight: 0.2,
    icon: "ShieldCheck",
  },
  income: {
    label: "Income Continuity",
    tagline: "Disability, emergency reserves, and key-person coverage.",
    weight: 0.2,
    icon: "TrendingUp",
  },
  retirement: {
    label: "Retirement Readiness",
    tagline: "Surviving-spouse income continuity and longevity plan.",
    weight: 0.15,
    icon: "Compass",
  },
  estate: {
    label: "Estate Documents",
    tagline: "Will, Trust, financial POA, and healthcare directive.",
    weight: 0.18,
    icon: "FileBadge",
  },
  beneficiaries: {
    label: "Beneficiaries",
    tagline: "Named, current, and properly structured.",
    weight: 0.15,
    icon: "Users",
  },
  education: {
    label: "Family Education",
    tagline: "Next generation prepared to receive.",
    weight: 0.12,
    icon: "BookOpen",
  },
};

export const DIMENSIONS: Dimension[] = [
  "protection",
  "income",
  "retirement",
  "estate",
  "beneficiaries",
  "education",
];

export type Choice = { value: string; label: string; points: number };

export type Question = {
  id: string;
  prompt: string;
  help?: string;
  choices: Choice[];
};

/** 5 substantive questions per dimension — 30 total. */
export const QUESTIONS: Record<Dimension, Question[]> = {
  protection: [
    {
      id: "face_self",
      prompt: "Roughly how much life insurance is in force on you today?",
      choices: [
        { value: "none", label: "None", points: 0 },
        { value: "lt_250k", label: "Under $250,000", points: 25 },
        { value: "250_500", label: "$250K – $500K", points: 50 },
        { value: "500_1m", label: "$500K – $1M", points: 75 },
        { value: "gt_1m", label: "Over $1M", points: 100 },
      ],
    },
    {
      id: "face_spouse",
      prompt: "How much life insurance is in force on your spouse / partner?",
      help: "Choose 'Not applicable' if single.",
      choices: [
        { value: "na", label: "Not applicable", points: 100 },
        { value: "none", label: "None", points: 0 },
        { value: "lt_250k", label: "Under $250,000", points: 30 },
        { value: "250_500", label: "$250K – $500K", points: 60 },
        { value: "gt_500", label: "Over $500K", points: 100 },
      ],
    },
    {
      id: "last_review",
      prompt: "When did an strategist last review your coverage?",
      choices: [
        { value: "never", label: "Never", points: 0 },
        { value: "5plus", label: "5+ years ago", points: 25 },
        { value: "2_5", label: "2 – 5 years ago", points: 60 },
        { value: "lt_2", label: "Within 2 years", points: 100 },
      ],
    },
    {
      id: "coverage_type",
      prompt: "What type of coverage do you primarily own?",
      choices: [
        { value: "unknown", label: "I'm not sure", points: 10 },
        { value: "term", label: "Term only", points: 55 },
        { value: "perm", label: "Permanent only", points: 70 },
        { value: "mix", label: "Mix of term and permanent", points: 100 },
      ],
    },
    {
      id: "living_benefits",
      prompt: "Does your policy include living benefits (critical illness, chronic illness)?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "unsure", label: "Not sure", points: 30 },
        { value: "yes", label: "Yes", points: 100 },
      ],
    },
  ],
  income: [
    {
      id: "disability",
      prompt: "Do you have disability income insurance in force?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "group_only", label: "Group only through employer", points: 50 },
        { value: "individual", label: "Individual policy", points: 90 },
        { value: "both", label: "Both individual and group", points: 100 },
      ],
    },
    {
      id: "emergency_months",
      prompt: "How many months of expenses are in your emergency reserve?",
      choices: [
        { value: "0", label: "Less than 1 month", points: 0 },
        { value: "1_3", label: "1 – 3 months", points: 35 },
        { value: "3_6", label: "3 – 6 months", points: 70 },
        { value: "6_plus", label: "6+ months", points: 100 },
      ],
    },
    {
      id: "key_person",
      prompt: "If you own a business, is there a key-person or buy-sell plan in place?",
      choices: [
        { value: "no_business", label: "I don't own a business", points: 100 },
        { value: "no", label: "No plan in place", points: 0 },
        { value: "informal", label: "Informal arrangement", points: 40 },
        { value: "funded", label: "Funded and documented", points: 100 },
      ],
    },
    {
      id: "second_income",
      prompt: "If your household lost one income tomorrow, how long could you cover essentials?",
      choices: [
        { value: "weeks", label: "A few weeks", points: 10 },
        { value: "months_3", label: "Around 3 months", points: 45 },
        { value: "months_6", label: "6+ months", points: 80 },
        { value: "year", label: "A year or more", points: 100 },
      ],
    },
    {
      id: "debt_band",
      prompt: "How would you describe your non-mortgage debt load?",
      choices: [
        { value: "heavy", label: "Heavy / increasing", points: 0 },
        { value: "moderate", label: "Moderate / steady", points: 50 },
        { value: "light", label: "Light / paying down", points: 85 },
        { value: "none", label: "None", points: 100 },
      ],
    },
  ],
  retirement: [
    {
      id: "accounts_funded",
      prompt: "Are your retirement accounts (401k / IRA) actively funded each month?",
      choices: [
        { value: "no", label: "Not currently", points: 0 },
        { value: "occasional", label: "Occasionally", points: 35 },
        { value: "regular", label: "Regularly under 15% of income", points: 70 },
        { value: "max", label: "Maxing contributions", points: 100 },
      ],
    },
    {
      id: "spouse_income_plan",
      prompt: "Is there a written plan for surviving-spouse income at retirement?",
      choices: [
        { value: "na", label: "Not applicable", points: 100 },
        { value: "no", label: "No", points: 0 },
        { value: "informal", label: "Informal understanding", points: 40 },
        { value: "documented", label: "Yes, documented", points: 100 },
      ],
    },
    {
      id: "guaranteed_income",
      prompt: "Do you have a pension, annuity, or other lifetime-income source planned?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "considering", label: "Considering it", points: 40 },
        { value: "partial", label: "Yes, covers part of retirement", points: 75 },
        { value: "full", label: "Yes, covers essentials", points: 100 },
      ],
    },
    {
      id: "replacement_pct",
      prompt: "What percent of current income do you expect to replace in retirement?",
      choices: [
        { value: "unknown", label: "I don't know", points: 10 },
        { value: "lt_40", label: "Less than 40%", points: 25 },
        { value: "40_70", label: "40 – 70%", points: 60 },
        { value: "70_plus", label: "70% or more", points: 100 },
      ],
    },
    {
      id: "healthcare_plan",
      prompt: "Do you have a plan for healthcare costs in retirement (before Medicare or LTC)?",
      choices: [
        { value: "no", label: "No plan", points: 0 },
        { value: "thinking", label: "Thinking about it", points: 30 },
        { value: "savings", label: "Earmarked savings (HSA, etc.)", points: 70 },
        { value: "policy", label: "Dedicated policy or strategy", points: 100 },
      ],
    },
  ],
  estate: [
    {
      id: "will",
      prompt: "Do you have a current Will?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "old", label: "Yes, but older than 5 years", points: 50 },
        { value: "recent", label: "Yes, updated in last 5 years", points: 100 },
      ],
    },
    {
      id: "trust",
      prompt: "Do you have a Trust in place?",
      choices: [
        { value: "none", label: "No trust", points: 0 },
        { value: "considering", label: "Considering", points: 25 },
        { value: "revocable", label: "Revocable living trust", points: 80 },
        { value: "irrevocable", label: "Irrevocable / advanced structure", points: 100 },
      ],
    },
    {
      id: "fin_poa",
      prompt: "Is a financial Power of Attorney named and documented?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "unsure", label: "Not sure", points: 25 },
        { value: "yes", label: "Yes", points: 100 },
      ],
    },
    {
      id: "healthcare_directive",
      prompt: "Do you have a healthcare directive (living will / healthcare POA)?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "unsure", label: "Not sure", points: 25 },
        { value: "yes", label: "Yes", points: 100 },
      ],
    },
    {
      id: "doc_storage",
      prompt: "Are your documents stored where a spouse / executor can find them?",
      choices: [
        { value: "no", label: "No / no documents", points: 0 },
        { value: "self_only", label: "Stored but only I know where", points: 35 },
        { value: "shared", label: "Spouse / executor knows location", points: 100 },
      ],
    },
  ],
  beneficiaries: [
    {
      id: "primary",
      prompt: "Are primary beneficiaries named on every policy and retirement account?",
      choices: [
        { value: "no", label: "No / unsure", points: 0 },
        { value: "some", label: "On some accounts", points: 50 },
        { value: "all", label: "Yes, on all accounts", points: 100 },
      ],
    },
    {
      id: "contingent",
      prompt: "Are contingent (backup) beneficiaries named?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "some", label: "On some accounts", points: 50 },
        { value: "all", label: "Yes, on all", points: 100 },
      ],
    },
    {
      id: "last_reviewed",
      prompt: "When were beneficiaries last reviewed?",
      choices: [
        { value: "never", label: "Never / can't recall", points: 0 },
        { value: "5plus", label: "5+ years ago", points: 25 },
        { value: "2_5", label: "2 – 5 years ago", points: 60 },
        { value: "lt_2", label: "Within last 2 years", points: 100 },
      ],
    },
    {
      id: "minors",
      prompt: "If you have minor children, how are inheritances structured for them?",
      choices: [
        { value: "na", label: "No minor children", points: 100 },
        { value: "outright", label: "Outright at 18", points: 20 },
        { value: "utma", label: "UTMA / UGMA account", points: 60 },
        { value: "trust", label: "Trust with named trustee", points: 100 },
      ],
    },
    {
      id: "alignment",
      prompt: "Do your beneficiary designations align with your Will?",
      choices: [
        { value: "no", label: "No / conflicting", points: 0 },
        { value: "unsure", label: "Not sure", points: 30 },
        { value: "yes", label: "Yes — reviewed together", points: 100 },
      ],
    },
  ],
  education: [
    {
      id: "heirs_know_advisor",
      prompt: "Do your heirs know who your trusted financial strategist is?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "spouse_only", label: "Spouse only", points: 50 },
        { value: "yes", label: "Yes — heirs introduced", points: 100 },
      ],
    },
    {
      id: "family_convo",
      prompt: "Has your family had an open conversation about your estate plan?",
      choices: [
        { value: "no", label: "Never", points: 0 },
        { value: "partial", label: "Bits and pieces", points: 45 },
        { value: "yes", label: "Yes — full conversation", points: 100 },
      ],
    },
    {
      id: "doc_location_shared",
      prompt: "Have you shared the location of key documents and account access with your spouse / executor?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "partial", label: "Partially", points: 50 },
        { value: "yes", label: "Yes — fully documented", points: 100 },
      ],
    },
    {
      id: "kids_literacy",
      prompt: "Is there a plan to teach the next generation about money and stewardship?",
      choices: [
        { value: "no_kids", label: "Not applicable", points: 100 },
        { value: "no", label: "No plan", points: 0 },
        { value: "informal", label: "Informal lessons", points: 55 },
        { value: "structured", label: "Structured, ongoing", points: 100 },
      ],
    },
    {
      id: "mission_statement",
      prompt: "Do you have a written family mission, values, or legacy statement?",
      choices: [
        { value: "no", label: "No", points: 0 },
        { value: "drafting", label: "Drafting one", points: 40 },
        { value: "yes", label: "Yes — shared with family", points: 100 },
      ],
    },
  ],
};

export type Responses = Record<string, string>;

export type DimensionScore = {
  dimension: Dimension;
  score: number;
  label: string;
  summary: string;
};

export type Band = "legacy_ready" | "strong_foundation" | "meaningful_gaps" | "critical";

export type ImprovementAction = {
  id: string;
  dimension: Dimension;
  title: string;
  recommendation: string;
  estimated_impact: number; // points
  cta_label: string;
  cta_href: string;
};

export type LegacyScoreAnalysis = {
  per_dimension: DimensionScore[];
  total: number;
  band: Band;
  band_label: string;
  band_message: string;
  improvement_actions: ImprovementAction[];
};

function scoreDimension(dim: Dimension, responses: Responses): number {
  const qs = QUESTIONS[dim];
  let total = 0;
  let count = 0;
  for (const q of qs) {
    const answer = responses[`${dim}.${q.id}`];
    const choice = q.choices.find((c) => c.value === answer);
    if (choice) {
      total += choice.points;
      count += 1;
    }
  }
  if (count === 0) return 0;
  return Math.round(total / count);
}

function summarize(dim: Dimension, score: number): string {
  const meta = DIMENSION_META[dim];
  if (score >= 85) return `${meta.label} is strong — keep it current with periodic reviews.`;
  if (score >= 70) return `${meta.label} has a solid base with a few refinements to make.`;
  if (score >= 50) return `${meta.label} has meaningful gaps worth addressing this year.`;
  if (score >= 25) return `${meta.label} needs attention — the highest-leverage area for you.`;
  return `${meta.label} is missing — this is the foundation to build first.`;
}

const BAND_LABELS: Record<Band, { label: string; message: string }> = {
  legacy_ready: {
    label: "Legacy Ready",
    message:
      "Your family's legacy infrastructure is in strong shape. Maintain it with an annual review.",
  },
  strong_foundation: {
    label: "Strong Foundation",
    message:
      "The core is in place. A few specific refinements will move you to fully Legacy Ready.",
  },
  meaningful_gaps: {
    label: "Meaningful Gaps",
    message:
      "Important elements are missing. Addressing them this year materially strengthens your family's position.",
  },
  critical: {
    label: "Critical Attention Needed",
    message:
      "Foundational work is recommended. Start with one or two essentials — it's the right order.",
  },
};

export function bandOf(total: number): Band {
  if (total >= 85) return "legacy_ready";
  if (total >= 70) return "strong_foundation";
  if (total >= 50) return "meaningful_gaps";
  return "critical";
}

const ACTION_BY_DIMENSION: Record<
  Dimension,
  { title: string; recommendation: string; cta_label: string; cta_href: string }
> = {
  protection: {
    title: "Close the protection gap",
    recommendation:
      "Have a Strategist model the exact coverage your income, debt, and education goals require — and the lowest-friction way to put it in force.",
    cta_label: "Schedule a coverage walkthrough",
    cta_href: "/book",
  },
  income: {
    title: "Protect the paycheck",
    recommendation:
      "Review individual disability coverage and emergency reserves. A disability is statistically more likely than an early death during working years.",
    cta_label: "Schedule an income-protection review",
    cta_href: "/book",
  },
  retirement: {
    title: "Build the surviving-spouse income plan",
    recommendation:
      "Map guaranteed income against essential expenses — both spouses, both lifespans. This is the single most common planning gap.",
    cta_label: "Schedule a retirement walkthrough",
    cta_href: "/book",
  },
  estate: {
    title: "Put the four documents in place",
    recommendation:
      "Will, Trust (where appropriate), financial POA, and healthcare directive. This is fixable in weeks, not years.",
    cta_label: "Get the estate checklist",
    cta_href: "/academy",
  },
  beneficiaries: {
    title: "Run a beneficiary audit",
    recommendation:
      "Beneficiary designations override your Will. Confirm primary and contingent on every policy, retirement account, and bank account.",
    cta_label: "Schedule a beneficiary audit",
    cta_href: "/book",
  },
  education: {
    title: "Prepare the next generation",
    recommendation:
      "Introduce heirs to your Strategist, share document locations, and document a short family mission statement. The plan only works if they can carry it.",
    cta_label: "Schedule a family conversation",
    cta_href: "/book",
  },
};

export function analyzeLegacyScore(responses: Responses): LegacyScoreAnalysis {
  const per_dimension: DimensionScore[] = DIMENSIONS.map((d) => {
    const s = scoreDimension(d, responses);
    return {
      dimension: d,
      score: s,
      label: DIMENSION_META[d].label,
      summary: summarize(d, s),
    };
  });

  const total = Math.round(
    per_dimension.reduce(
      (acc, ds) => acc + ds.score * DIMENSION_META[ds.dimension].weight,
      0,
    ),
  );

  const band = bandOf(total);
  const bandInfo = BAND_LABELS[band];

  // Top-3 lowest dimensions by score → highest impact actions.
  const sorted = [...per_dimension].sort((a, b) => a.score - b.score);
  const top = sorted.slice(0, 3);
  const improvement_actions: ImprovementAction[] = top.map((ds) => {
    const w = DIMENSION_META[ds.dimension].weight;
    // Points the total would gain if this dimension reached 100, rounded.
    const impact = Math.max(1, Math.round((100 - ds.score) * w));
    const tpl = ACTION_BY_DIMENSION[ds.dimension];
    return {
      id: ds.dimension,
      dimension: ds.dimension,
      title: tpl.title,
      recommendation: tpl.recommendation,
      estimated_impact: impact,
      cta_label: tpl.cta_label,
      cta_href: tpl.cta_href,
    };
  });

  return {
    per_dimension,
    total,
    band,
    band_label: bandInfo.label,
    band_message: bandInfo.message,
    improvement_actions,
  };
}

export function bandTone(band: Band): "success" | "bronze" | "warning" | "danger" {
  if (band === "legacy_ready") return "success";
  if (band === "strong_foundation") return "bronze";
  if (band === "meaningful_gaps") return "warning";
  return "danger";
}